/**
 * ChartFrame — a titled card wrapping a Plotly viz (§15, viz stack = Plotly).
 * Each consumer passes its own `data` series + optional `layout`; ChartFrame
 * supplies the card chrome, a token-driven theme (transparent backgrounds,
 * theme-neutral axes, monospace ticks), responsive autosize, and a PNG export
 * config with a transparent background.
 *
 * Plotly is heavy (~1MB) so the actual <Plot> is lazy-loaded — the ChartFrame
 * card + a placeholder render immediately, and the plotly chunk streams in when
 * the chart mounts (isolated in its own vite chunk).
 *
 * Axis labels are REQUIRED by the charter (§20 punch-list items 5/9): pass
 * `xTitle` / `yTitle` (or full `layout.xaxis.title`). ChartFrame does not
 * invent data — consumers own their series.
 */
import { Suspense, lazy, useMemo, type ReactNode } from "react";
import type { Data, Layout, Config } from "plotly.js-dist-min";
import { useChartTheme } from "./useChartTheme";
import "./chart.css";

// Lazy so the plotly chunk is only fetched when a chart actually renders.
const Plot = lazy(() => import("./LazyPlot"));

export interface ChartFrameProps {
  /** Card title. */
  title: ReactNode;
  /** Optional right-aligned subtitle / unit note. */
  subtitle?: ReactNode;
  /** Optional header action (e.g. a series toggle). */
  action?: ReactNode;
  /** Plotly data series — the consumer owns these. */
  data: Data[];
  /** Extra Plotly layout, deep-merged over the themed defaults. */
  layout?: Partial<Layout>;
  /** X axis title (charter requires labelled axes). */
  xTitle?: string;
  /** Y axis title. */
  yTitle?: string;
  /** Fixed plot height (px). Default 260. */
  height?: number;
  /** Extra Plotly config. */
  config?: Partial<Config>;
  /** aria-label for the figure. */
  ariaLabel?: string;
}

export function ChartFrame({
  title,
  subtitle,
  action,
  data,
  layout,
  xTitle,
  yTitle,
  height = 260,
  config,
  ariaLabel,
}: ChartFrameProps) {
  const theme = useChartTheme();

  const themedLayout = useMemo<Partial<Layout>>(() => {
    const axis = {
      color: theme.dim,
      gridcolor: theme.grid,
      linecolor: theme.line,
      zerolinecolor: theme.line,
      tickfont: { family: theme.fontMono, size: 11, color: theme.dim },
      titlefont: { family: theme.fontSans, size: 12, color: theme.ink },
    };
    return {
      autosize: true,
      height,
      margin: { l: 56, r: 16, t: 10, b: 44 },
      paper_bgcolor: "rgba(0,0,0,0)",
      plot_bgcolor: "rgba(0,0,0,0)",
      font: { family: theme.fontSans, color: theme.ink, size: 12 },
      colorway: [theme.oxboZ, theme.good, theme.oxboM, theme.warn, theme.oxboU, theme.crit],
      xaxis: { ...axis, ...(xTitle ? { title: { text: xTitle } } : {}) },
      yaxis: { ...axis, ...(yTitle ? { title: { text: yTitle } } : {}) },
      legend: { font: { color: theme.dim, size: 11 }, orientation: "h", y: -0.25 },
      hoverlabel: { bgcolor: theme.line, font: { family: theme.fontMono, color: theme.ink } },
      ...layout,
    };
  }, [theme, height, xTitle, yTitle, layout]);

  const themedConfig = useMemo<Partial<Config>>(
    () => ({
      displaylogo: false,
      responsive: true,
      // PNG export with a transparent background — theme-neutral, per §15.
      toImageButtonOptions: { format: "png", filename: "chart", scale: 2 },
      modeBarButtonsToRemove: ["lasso2d", "select2d"],
      ...config,
    }),
    [config],
  );

  return (
    <div className="chartframe">
      <div className="chartframe-head">
        <h3>{title}</h3>
        {subtitle ? <span className="sub">{subtitle}</span> : null}
        {action ? <span className="actions">{action}</span> : null}
      </div>
      <div className="chartframe-plot" aria-label={ariaLabel}>
        <Suspense fallback={<div className="chartframe-placeholder">Loading chart…</div>}>
          <Plot
            data={data}
            layout={themedLayout}
            config={themedConfig}
            useResizeHandler
            style={{ width: "100%", height: `${height}px` }}
          />
        </Suspense>
      </div>
    </div>
  );
}
